import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Body, Button, Heading, I, Stack } from '@leanin/ui';
import { FlowShell, type FlowStep } from '@/components/onboarding/FlowShell';
import { trackOnboardingStepCompleted } from '@/lib/analytics/events';
import { flowStepParams } from '@/lib/analytics/onboarding-steps';
import { LoopVideo } from '@/components/media/LoopVideo';
import welcomeMp4 from '@/assets/onboarding/welcome.mp4';
import welcomeWebm from '@/assets/onboarding/welcome.webm';
import welcomePoster from '@/assets/onboarding/welcome-poster.png?w=440;880;1320&format=avif;webp;png&as=picture';

const STEPS: ReadonlyArray<FlowStep> = [
  { label: 'Welcome' },
  { label: "Your Circle's page" },
  { label: "You're in" },
];

export const Route = createFileRoute('/onboarding/cl/welcome')({
  component: CLOnboardingWelcomePage,
});

function CLOnboardingWelcomePage() {
  const navigate = useNavigate();
  return (
    <FlowShell
      role="Circle Leader"
      stepIndex={0}
      steps={STEPS}
      minutes={3}
      title="Welcome to the Lean In community — we are SO glad you’re here."
      primary={
        <Button
          variant="primary"
          icon={I.arrow}
          onClick={() => {
            trackOnboardingStepCompleted(
              flowStepParams('/onboarding/cl/welcome'),
            );
            navigate({ to: '/onboarding/cl/create-circle' });
          }}
        >
          Get started
        </Button>
      }
    >
      <Stack gap="lg">
        <LoopVideo
          webm={welcomeWebm}
          mp4={welcomeMp4}
          poster={welcomePoster}
          aria-label="Welcome to Lean In Circles"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />

        <Stack gap="md">
          <Heading level={2} style={{ fontStyle: 'italic' }}>
            Dear Circle Leader,
          </Heading>
          <Body size="md">
            You just decided to lead a Circle. That’s a big deal. Whether you
            signed up with total confidence or a small flutter of{' '}
            <em>what have I done?</em> — know this: you’re in the right place,
            and you’re not doing this alone.
          </Body>
          <Body size="md">
            <strong style={{ color: 'var(--primary)' }}>
              30,000+ new Circles
            </strong>{' '}
            were started last year.{' '}
            <strong style={{ color: 'var(--primary)' }}>
              98% of Circle Leaders
            </strong>{' '}
            say it had a positive impact on their life. You’re about to find
            out why — and the people you bring into your Circle will feel it
            too, because of you.
          </Body>
          <Body size="md">
            Leading a Circle is simpler than you think. One meeting a month. A
            group of 8–12 people. Lean In gives you everything else. You don’t
            need to be an expert. You just need to begin.
          </Body>
          <Body size="md">— The Lean In Team</Body>
        </Stack>
      </Stack>
    </FlowShell>
  );
}
